import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { HiXMark, HiMagnifyingGlassPlus, HiChevronLeft, HiChevronRight, HiArrowUpRight } from 'react-icons/hi2';
import { getGalleryItems, getGalleryCategories } from '../data/galleryData';

export default function Gallery() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const loadGallery = async () => {
      const items = await getGalleryItems();
      const cats = await getGalleryCategories();
      setGalleryItems(items);
      setCategories(cats);
    };
    loadGallery();
  }, []);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxIndex]);

  const filteredItems = activeFilter === 'all' ? galleryItems : galleryItems.filter(item => item.category === activeFilter);

  const filters = [
    { key: 'all', label: 'All Media' },
    ...categories.map(cat => ({ key: cat.slug, label: cat.name }))
  ];

  const handleNext = (e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % filteredItems.length); };
  const handlePrev = (e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length); };

  const categoryGradients = {
    sales: 'from-primary to-primary-dark',
    rental: 'from-secondary to-secondary-dark',
    panels: 'from-[#1A2535] to-[#0C1A12]',
    solar: 'from-[#2B4530] to-[#1A2B20]',
    telecom: 'from-[#2B3540] to-[#151F28]',
  };

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/gallery.jpg')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Media Showcase</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Engineering <span className="text-gradient-gold">Gallery</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Visual logs of on-site silent generator commissions, electrical panels, and solar setups.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="bg-white border-b border-border sticky top-14 z-30 shadow-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-2 overflow-x-auto py-4">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => { setActiveFilter(filter.key); setLightboxIndex(null); }}
              className={`px-5 py-2.5 rounded-full whitespace-nowrap text-xs font-bold uppercase tracking-wider transition-all duration-300 flex-shrink-0 ${activeFilter === filter.key
                  ? 'bg-primary text-white shadow-premium'
                  : 'bg-light-section text-body hover:text-primary border border-border'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Grid */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item, index) => (
            <div
              key={index}
              onClick={() => setLightboxIndex(index)}
              className="bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 hover:border-primary/20 transition-all duration-300 cursor-pointer group"
            >
              {/* Media Visual */}
              <div className="aspect-video relative flex items-center justify-center text-white overflow-hidden bg-light-section">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${categoryGradients[item.category] || categoryGradients.sales} flex flex-col items-center justify-center`}>
                    <span className="text-[9px] text-white/50 font-bold uppercase tracking-widest">{item.category}</span>
                  </div>
                )}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 flex items-center justify-center">
                  <HiMagnifyingGlassPlus className="w-8 h-8 text-white scale-75 group-hover:scale-100 transition-all duration-300" />
                </div>
              </div>

              <div className="p-6 flex flex-col justify-between flex-grow">
                <div>
                  <h4 className="text-xs font-bold text-heading mb-2 uppercase tracking-wide leading-tight group-hover:text-primary transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-xs text-body leading-relaxed mb-5">{item.desc}</p>
                </div>
                <Link
                  to="/contact#enquiry"
                  onClick={(e) => e.stopPropagation()}
                  className="btn-primary w-full justify-center py-2.5 rounded-xl text-[10px]"
                >
                  Enquire for Similar Setup
                  <HiArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-heading/95 backdrop-blur-md flex items-center justify-center p-6 overflow-hidden"
          onClick={() => setLightboxIndex(null)}
        >
          <button onClick={() => setLightboxIndex(null)} className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <HiXMark className="w-6 h-6" />
          </button>
          <button onClick={handlePrev} className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/6 hover:bg-white/15 text-white transition-colors">
            <HiChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={handleNext} className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/6 hover:bg-white/15 text-white transition-colors">
            <HiChevronRight className="w-6 h-6" />
          </button>

          <div
            className="max-w-xl w-full bg-white rounded-3xl border border-border text-heading relative overflow-hidden flex flex-col max-h-[85vh] shadow-premium"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full overflow-hidden flex items-center justify-center bg-transparent rounded-t-3xl flex-shrink-0">
              {filteredItems[lightboxIndex].image ? (
                <img
                  src={filteredItems[lightboxIndex].image}
                  alt={filteredItems[lightboxIndex].title}
                  className="w-full max-h-[28vh] md:max-h-[38vh] lg:max-h-[45vh] object-contain"
                />
              ) : (
                <div className={`w-full h-[150px] md:h-[220px] bg-gradient-to-br ${categoryGradients[filteredItems[lightboxIndex].category] || categoryGradients.sales} flex items-center justify-center`}>
                  <div className="text-center">
                    <span className="badge-dark text-[9px] mb-3 block mx-auto w-fit">{filteredItems[lightboxIndex].category}</span>
                    <span className="text-xs text-accent font-bold uppercase tracking-widest">Green Gusto Live Site</span>
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 md:p-6 overflow-y-auto">
              <span className="badge-primary text-[9px] mb-2 w-fit block uppercase tracking-wider">{filteredItems[lightboxIndex].category}</span>
              <h3 className="text-base md:text-lg font-display font-extrabold mb-3 text-heading">{filteredItems[lightboxIndex].title}</h3>
              <p className="text-xs md:text-sm text-body leading-relaxed">{filteredItems[lightboxIndex].desc}</p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
